import { HeartIcon, ShareIcon } from "@/components/Icons";
import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent } from "@/components/ui/Tooltip";
import { Locale } from "@/enums/common.enum";
import { useAddToFavorites, useRemoveFavorite } from "@/hooks/useFavorite";
import { useSearchParams } from "@/hooks/useSearchParams";
import { Property } from "@/interfaces/property.interface";
import { gTagEvents } from "@/lib/gtag";
import { makePropertyLink } from "@/lib/property/helpers";
import { useAuth } from "@clerk/nextjs";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import { MouseEvent } from "react";
import { toast } from "sonner";

interface PropertyQuickActionProps {
  property: Property;
  iconSize?: string; // pass tailwind sizes here
  className?: string;
}

export const PropertyQuickActions = ({
  property,
  className,
}: PropertyQuickActionProps) => {
  const locale = useLocale() as Locale;
  const t = useTranslations("listing-detail");
  const st = useTranslations("shared");
  const ft = useTranslations("favorite");

  const searchParams = useSearchParams();
  const { isSignedIn } = useAuth();

  const addToFavorites = useAddToFavorites();
  const removeFromFavorite = useRemoveFavorite();

  const isPending = addToFavorites.isPending || removeFromFavorite.isPending;
  const referrer = searchParams.get("referrer") as string;

  const handleShareClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const propertyLink = makePropertyLink(property, locale, "share");
    navigator.clipboard.writeText(`${baseUrl}/${locale}${propertyLink}`);

    toast.success(t("quick-action.shared"));
  };

  const handleFavoriteClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    if (!property.isFavorite && isSignedIn) {
      addToFavorites.mutateAsync(
        { id: property.id },
        {
          onSuccess: () => {
            gTagEvents.addToWishList(property, referrer);
            property.isFavorite = true;
          },
        }
      );
    } else {
      removeFromFavorite.mutateAsync(
        { id: property.id },
        {
          onSuccess: () => {
            property.isFavorite = false;
          },
        }
      );
    }
  };

  return (
    <div className={clsx("flex flex-row items-center gap-2.5", className)}>
      <Button variant={"outline"} onClick={handleShareClick} className="sm:p-2">
        <ShareIcon className={"size-4"} />
        <span className="hidden sm:block">{st("words.share")}</span>
      </Button>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Button
            variant={"outline"}
            className="sm:p-2"
            onClick={handleFavoriteClick}
            disabled={!isSignedIn}
          >
            <HeartIcon
              className={clsx("size-4", {
                "fill-gray-600": !property.isFavorite,
                "fill-primary": property.isFavorite,
                "animate-pulse": isPending,
              })}
            />
            <span className="hidden sm:block">{st("words.save")}</span>
          </Button>
        </TooltipTrigger>
        {!isSignedIn && <TooltipContent>{ft("action.click")}</TooltipContent>}
      </Tooltip>
    </div>
  );
};

export const PropertyShareAction = ({ property }: { property: Property }) => {
  const locale = useLocale() as Locale;
  const t = useTranslations("listing-detail");

  const handleShareClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const propertyLink = makePropertyLink(property, locale, "share");
    navigator.clipboard.writeText(`${baseUrl}/${locale}${propertyLink}`);

    toast.success(t("quick-action.shared"));
  };

  return (
    <div className={"flex flex-row items-center gap-2.5"}>
      <span
        className="rounded-full fill-gray-600 transition-colors hover:fill-gray-900 cursor-pointer"
        onClick={handleShareClick}
      >
        <ShareIcon className={"size-4"} />
      </span>
    </div>
  );
};
